import React, { useState, useRef } from 'react';
import { emojify, search } from 'node-emoji';
import './AddContentModal.css';

const AddContentModal = ({ onClose, onAdd }) => {
  const [content, setContent] = useState('');
  const [caption, setCaption] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emojiSuggestions, setEmojiSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [currentField, setCurrentField] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  const contentRef = useRef(null);
  const captionRef = useRef(null);

  // Auto-replace emojis as you complete typing them
  const processEmojiInput = (text, cursorPos, setValue) => {
    // Find :word: pattern before cursor
    const beforeCursor = text.substring(0, cursorPos);
    const match = beforeCursor.match(/:([a-zA-Z_]+):$/);
    
    if (match) {
      // Complete emoji found, replace it
      const emojiName = match[1];
      const emojiChar = emojify(`:${emojiName}:`);
      
      if (emojiChar !== `:${emojiName}:`) { // Valid emoji
        const newText = text.substring(0, match.index) + emojiChar + text.substring(cursorPos);
        setValue(newText);
        setShowSuggestions(false);
        
        // Set cursor position after emoji
        setTimeout(() => {
          const newPos = match.index + emojiChar.length;
          if (currentField === 'content' && contentRef.current) {
            contentRef.current.setSelectionRange(newPos, newPos);
          } else if (currentField === 'caption' && captionRef.current) {
            captionRef.current.setSelectionRange(newPos, newPos);
          }
        }, 0);
        return;
      }
    }

    // Check for partial emoji typing
    const partialMatch = beforeCursor.match(/:([a-zA-Z_]*)$/);
    
    if (partialMatch && partialMatch[1].length > 0) {
      // Show suggestions
      const query = partialMatch[1];
      const suggestions = search(query).slice(0, 8); // Limit to 8 suggestions
      
      if (suggestions.length > 0) {
        setEmojiSuggestions(suggestions);
        setShowSuggestions(true);
        setSuggestionIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setContent(newValue);
    setCurrentField('content');
    setCursorPosition(cursorPos);
    processEmojiInput(newValue, cursorPos, setContent);
  };

  const handleCaptionChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setCaption(newValue);
    setCurrentField('caption');
    setCursorPosition(cursorPos);
    processEmojiInput(newValue, cursorPos, setCaption);
  };

  const selectSuggestion = (suggestion) => {
    const setValue = currentField === 'content' ? setContent : setCaption;
    const currentValue = currentField === 'content' ? content : caption;
    
    // Find the partial emoji before cursor
    const beforeCursor = currentValue.substring(0, cursorPosition);
    const match = beforeCursor.match(/:([a-zA-Z_]*)$/);
    
    if (match) {
      const newText = currentValue.substring(0, match.index) + suggestion.emoji + currentValue.substring(cursorPosition);
      setValue(newText);
      setShowSuggestions(false);
      
      // Set cursor position after emoji
      setTimeout(() => {
        const newPos = match.index + suggestion.emoji.length;
        if (currentField === 'content' && contentRef.current) {
          contentRef.current.setSelectionRange(newPos, newPos);
        } else if (currentField === 'caption' && captionRef.current) {
          captionRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSuggestionIndex(prev => (prev + 1) % emojiSuggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSuggestionIndex(prev => (prev - 1 + emojiSuggestions.length) % emojiSuggestions.length);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        selectSuggestion(emojiSuggestions[suggestionIndex]);
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        // Do nothing for other keys
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setError('Please enter some content');
      return;
    }

    if (!passphrase.trim()) {
      setError('Please enter the passphrase');
      return;
    }

    setLoading(true);
    setError('');

    const result = await onAdd({
      content: content.trim(),
      caption: caption.trim() || undefined,
      passphrase: passphrase.trim()
    });

    if (result.success) {
      setContent('');
      setCaption('');
      setPassphrase('');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add to Paul Likes</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <div className="input-with-suggestions">
              <textarea
                ref={contentRef}
                id="content"
                value={content}
                onChange={handleContentChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter text, image URL, or website link..."
                required
                disabled={loading}
                autoComplete="off"
              />
              {showSuggestions && currentField === 'content' && (
                <div className="emoji-suggestions">
                  {emojiSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.key}
                      className={`suggestion-item ${index === suggestionIndex ? 'active' : ''}`}
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <span className="suggestion-emoji">{suggestion.emoji}</span>
                      <span className="suggestion-name">:{suggestion.key}:</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-hint">
              Examples: "I love Austin :heart:" • "https://example.com/photo.jpg" • ":pizza: is amazing!"
              <br />
              <span className="emoji-help">Type : followed by emoji name for autocomplete</span>
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="caption">Caption (optional)</label>
            <div className="input-with-suggestions">
              <input
                ref={captionRef}
                type="text"
                id="caption"
                value={caption}
                onChange={handleCaptionChange}
                onKeyDown={handleKeyDown}
                placeholder="Add a description or note... :sparkles:"
                disabled={loading}
                autoComplete="off"
              />
              {showSuggestions && currentField === 'caption' && (
                <div className="emoji-suggestions">
                  {emojiSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.key}
                      className={`suggestion-item ${index === suggestionIndex ? 'active' : ''}`}
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <span className="suggestion-emoji">{suggestion.emoji}</span>
                      <span className="suggestion-name">:{suggestion.key}:</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="passphrase">Passphrase *</label>
            <input
              type="text"
              id="passphrase"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Enter secret passphrase"
              required
              disabled={loading}
              autoComplete="off"
            />
            <div className="form-hint">
              Required to add content (ask Paul for the secret phrase)
            </div>
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Content'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContentModal;